/**
 * Converts API hours format to schema.org OpeningHoursSpecification format
 * Only considers office hours, ignores access hours
 */

const DAYS_OF_WEEK = [
	"Monday",
	"Tuesday",
	"Wednesday",
	"Thursday",
	"Friday",
	"Saturday",
	"Sunday"
];

/**
 * Converts time from "HH:MM:SS" to "HH:MM" format
 * @param {string} time - Time in "HH:MM:SS" format
 * @returns {string|null} - Time in "HH:MM" format or null if invalid
 */
const formatTimeToHHMM = (time) => {
	if (!time || time === "-") {
		return null;
	}
	const parts = time.split(":");
	if (parts.length < 2) {
		return null;
	}
	return `${parts[0]}:${parts[1]}`;
};

/**
 * Checks if a day is closed based on open/closed values
 * @param {Object} dayHours - Day hours object with open/closed properties
 * @returns {boolean}
 */
const isDayClosed = (dayHours) => {
	if (!dayHours) return true;
	const { open, closed } = dayHours;
	return open === "-" || closed === "-" || open === closed;
};

/**
 * Converts hours object from API response to OpeningHoursSpecification format
 * @param {Object} hours - Hours object from API response
 * @returns {Array} - Array of OpeningHoursSpecification objects
 */
export const formatOpeningHours = (hours) => {
	try {
		if (!hours?.office) {
			return [];
		}

		const { office } = hours;
		const hoursMap = new Map();

		// Group days by their open/close times
		DAYS_OF_WEEK.forEach((day) => {
			const dayHours = office[day.toLowerCase()];

			if (isDayClosed(dayHours)) {
				return; // Skip closed days
			}

			const opens = formatTimeToHHMM(dayHours.open);
			const closes = formatTimeToHHMM(dayHours.closed);

			if (!opens || !closes) {
				return; // Skip invalid times
			}

			const key = `${opens}-${closes}`;

			if (hoursMap.has(key)) {
				hoursMap.get(key).dayOfWeek.push(day);
			} else {
				hoursMap.set(key, {
					"@type": "OpeningHoursSpecification",
					dayOfWeek: [day],
					opens,
					closes
				});
			}
		});

		return Array.from(hoursMap.values());
	} catch (error) {
		console.error("Error formatting opening hours:", error);
		return [];
	}
};

/**
 * Formats opening hours for all listings in the search response
 * @param {Array} listings - Array of listing objects with hours property
 * @returns {Array} - Array of listings with openingHoursSpecification added
 */
export const formatListingsOpeningHours = (listings) => {
	if (!Array.isArray(listings)) {
		return [];
	}

	return listings.map((listing) => {
		if (!listing?.hours) {
			return listing;
		}

		return {
			...listing,
			openingHoursSpecification: formatOpeningHours(listing.hours)
		};
	});
};

export default formatOpeningHours;
